import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import ScrollToTop from "./components/ScrollToTop";
import Home from "./pages/Home";
import Services from "./pages/Services";
import About from "./pages/About";
import FAQ from "./pages/FAQ";
import Contact from "./pages/Contact";
import NewClient from "./pages/NewClient";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import ScheduleAppointment from "./pages/ScheduleAppointment";
import NotFound from "./pages/NotFound";
import Login from "./pages/auth/Login";
import Signup from "./pages/auth/Signup";
import AdminLogin from "./pages/admin/Login";
import ClientDashboard from "./pages/client-portal/Dashboard";
import ClientAppointments from "./pages/client-portal/Appointments";
import ClientDocuments from "./pages/client-portal/Documents";
import ClientProfile from "./pages/client-portal/Profile";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminClients from "./pages/admin/Clients";
import AdminChat from "./pages/admin/Chat";
import AdminDocuments from "./pages/admin/Documents";
import AdminScheduling from "./pages/admin/Scheduling";
import AdminSchedulingCalendar from "./pages/admin/SchedulingCalendar";
import AdminReports from "./pages/admin/Reports";
import AdminIntakeForms from "./pages/admin/IntakeForms";
import AdminReviews from "./pages/admin/Reviews";
import AdminSettings from "./pages/admin/Settings";
import AdminAvailability from "./pages/admin/Availability";
import AdminEmail from "./pages/admin/Email";
import ClientEmailHistory from "./pages/client-portal/EmailHistory";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import AdminRoute from "./components/auth/AdminRoute";
import { getErrorReporter } from "./lib/errorReporter";

const queryClient = new QueryClient();

// Component to track route changes
const RouteTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    try {
      const reporter = getErrorReporter();
      reporter.addBreadcrumb({
        category: 'navigation',
        message: `Navigated to ${location.pathname}`,
        data: { 
          pathname: location.pathname,
          search: location.search,
        },
      });
    } catch (err) {
      // Reporter not initialized yet, ignore
    }
  }, [location]);
  
  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <RouteTracker />
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/about" element={<About />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/new-client" element={<NewClient />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/schedule-appointment" element={<ScheduleAppointment />} />
          <Route path="/auth/login" element={<Login />} />
          <Route path="/auth/signup" element={<Signup />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/client-portal" element={<ProtectedRoute><ClientDashboard /></ProtectedRoute>} />
          <Route path="/client-portal/profile" element={<ProtectedRoute><ClientProfile /></ProtectedRoute>} />
          <Route path="/client-portal/appointments" element={<ProtectedRoute><ClientAppointments /></ProtectedRoute>} />
          <Route path="/client-portal/documents" element={<ProtectedRoute><ClientDocuments /></ProtectedRoute>} />
          <Route path="/client-portal/email-history" element={<ProtectedRoute><ClientEmailHistory /></ProtectedRoute>} />
          <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path="/admin/clients" element={<AdminRoute><AdminClients /></AdminRoute>} />
          <Route path="/admin/chat" element={<AdminRoute><AdminChat /></AdminRoute>} />
          <Route path="/admin/documents" element={<AdminRoute><AdminDocuments /></AdminRoute>} />
            <Route path="/admin/scheduling" element={<AdminRoute><AdminScheduling /></AdminRoute>} />
            <Route path="/admin/scheduling/calendar" element={<AdminRoute><AdminSchedulingCalendar /></AdminRoute>} />
            <Route path="/admin/reports" element={<AdminRoute><AdminReports /></AdminRoute>} />
          <Route path="/admin/availability" element={<AdminRoute><AdminAvailability /></AdminRoute>} />
          <Route path="/admin/email" element={<AdminRoute><AdminEmail /></AdminRoute>} />
          <Route path="/admin/intake" element={<AdminRoute><AdminIntakeForms /></AdminRoute>} />
          <Route path="/admin/reviews" element={<AdminRoute><AdminReviews /></AdminRoute>} />
          <Route path="/admin/settings" element={<AdminRoute><AdminSettings /></AdminRoute>} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
